import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Input, Card, Layout, message } from 'antd';
import PageTopbar from '@/components/pagetopbar';
import usePageBack from '@/utils/usePageBack';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { fetchModify, fetchLogout } from '@/apis/user';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '@/store/modules/user';
import { getRequestErrorMessage } from '@/utils/requestError';

import './index.scss';


const { Content } = Layout;

const Modify = () => {
  // 配置初始化
  const navigate = useNavigate();
  const goBack = usePageBack('/personal/info');
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [submitting, setSubmitting] = useState(false);
  const passwordChangedRef = useRef(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(state => state.user);
  // 监听屏幕宽度变化，响应式切换页面布局
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // 用户点击修改密码后处理修改密码请求
  const onFinish = async values => {
    setSubmitting(true);
    try {
      await fetchModify(values);
    } catch (error) {
      message.error(getRequestErrorMessage(error, '密码修改失败，请稍后重试'));
      setSubmitting(false);
      return;
    }

    // 注销会更新认证状态，先标记成功，避免当前页面的访问守卫弹出误导性提示。
    passwordChangedRef.current = true;
    try {
      await dispatch(fetchLogout());
    } catch (error) {
      // 即使服务端注销出现意外异常，也要清除本地会话。
      dispatch(logout());
    }
    form.resetFields();
    message.success('密码修改成功，请重新登录');
    navigate('/login', { replace: true });
    setSubmitting(false);
  };
  // 用户没登录就访问的话提示用户并跳转到登录页
  if (!isAuthenticated && location.pathname === '/personal/modify' && !passwordChangedRef.current) {
    message.warning('请登录后再访问该页面');
    return <Navigate to="/login" replace />;
  }
  return (
    <div className="modify">
      {/* 移动端显示头部返回按钮 */}
      {isMobile ?
        <PageTopbar className="modify-mobile-topbar" title="修改密码" onBack={goBack} /> : null}
      {/* 修改密码框 */}
      <Content>
        <Card title={isMobile ? undefined : '修改密码'} className="modify-card">
          <Form
            className="modify-form"
            labelAlign="left"
            form={form}
            name="modify"
            onFinish={onFinish}
            scrollToFirstError
          >
            {/* 旧密码输入框 */}
            <Form.Item
              name="old_password"
              label="旧密码"
              rules={[
                {
                  required: true,
                  message: '请输入旧密码!',
                },
              ]}
              hasFeedback
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            {/* 新密码输入框 */}
            <Form.Item
              name="new_password"
              label="新密码"
              rules={[
                {
                  required: true,
                  message: '请输入新密码!',
                },
              ]}
              hasFeedback
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            {/* 确认新密码输入框 */}
            <Form.Item
              name="confirm_password"
              label="确认新密码"
              dependencies={['new_password']}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: '请确认新密码!',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的新密码不一致!'));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            {/* 修改密码按钮 */}
            <Form.Item className="modify-submit">
              <Button type="primary" htmlType="submit" loading={submitting}>
                修改密码
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Content>
    </div>
  );
};
export default Modify;
